import {EventEmitter, Injectable} from '@angular/core';
import * as shortid from 'shortid';
import {SessionService} from '../../authentication/session-service.service';
import {UserSettingsService} from '../../user-settings.service';
import {MavenSettingsTemplate, MavenTemplateSettings, MavenTemplateEntry, MavenTemplateReference, MavenTemplateScope} from './maven-settings-template';
import {MavenSharedTemplateClient} from './maven-shared-template-client';
import {assertMavenTemplateSettings} from './maven-template-validation';

export function mavenTemplatePrefix(underlying: string): string {
    return 'maven.templates.' + encodeURIComponent(underlying) + '.';
}
function lastUsedKey(underlying: string): string { return 'maven.last-used-template.' + encodeURIComponent(underlying); }
function copy<T>(value: T): T { return JSON.parse(JSON.stringify(value)); }
export function templateEntry(template: MavenSettingsTemplate, scope: MavenTemplateScope): MavenTemplateEntry {
    return {template: copy(template), scope, key: scope + ':' + template.id,
        label: template.name + (scope === 'shared' ? ' (Shared)' : '')};
}
@Injectable({providedIn: 'root'})
export class MavenTemplateService {
    readonly changed$ = new EventEmitter<void>();
    constructor(private readonly _settings: UserSettingsService, private readonly _session: SessionService,
        private readonly _shared: MavenSharedTemplateClient) {}

    personal(underlying: string): MavenSettingsTemplate[] {
        if (!underlying) { return []; }
        const prefix = mavenTemplatePrefix(underlying);
        return this._settings.getItemsMatchingPattern<MavenSettingsTemplate>(
            key => key.indexOf(prefix) === 0, this._session.sessionData.userId)
            .filter(row => !!row.value && (row.value.version === 1 || row.value.version === 2) && row.value.underlying === underlying
                && row.key === prefix + row.value.id && !!row.value.settings)
            .map(row => copy(row.value));
    }
    async list(underlying: string): Promise<MavenTemplateEntry[]> {
        if (!underlying) { return []; }
        const shared = await this._shared.list(underlying);
        return this.personal(underlying).map(t => templateEntry(t, 'personal'))
            .concat(shared.map(t => templateEntry(t, 'shared'))).sort((a, b) => a.label.localeCompare(b.label));
    }
    lastUsed(underlying: string, entries: MavenTemplateEntry[]): MavenTemplateEntry | null {
        const ref = this._settings.getValue<MavenTemplateReference>(lastUsedKey(underlying));
        return ref ? entries.filter(e => e.scope === ref.scope && e.template.id === ref.templateId)[0] || null : null;
    }
    async remember(underlying: string, entry: MavenTemplateEntry | null): Promise<void> {
        if (entry) {
            await this._settings.setValueConfirmed(lastUsedKey(underlying), {scope: entry.scope, templateId: entry.template.id});
        } else { await this._settings.deleteValueConfirmed(lastUsedKey(underlying)); }
    }
    async save(underlying: string, name: string, settings: MavenTemplateSettings,
        scope: MavenTemplateScope, previous?: MavenTemplateEntry): Promise<MavenTemplateEntry> {
        assertMavenTemplateSettings(settings);
        return this.persist(underlying, name, copy(settings), scope, previous);
    }
    // Metadata edits preserve the existing payload, including legacy incomplete preferences.
    async editMetadata(underlying: string, name: string, scope: MavenTemplateScope,
        previous: MavenTemplateEntry): Promise<MavenTemplateEntry> {
        if (!previous) { throw new Error('Choose a Template to Edit.'); }
        return this.persist(underlying, name, copy(previous.template.settings), scope, previous);
    }
    private async persist(underlying: string, name: string, settings: MavenTemplateSettings,
        scope: MavenTemplateScope, previous?: MavenTemplateEntry): Promise<MavenTemplateEntry> {
        name = (name || '').trim();
        if (!underlying || !name || name.length > 200) { throw new Error('Provide a Template Name (up to 200 Characters).'); }
        if ((scope === 'shared' || previous?.scope === 'shared') && !this._session.isSuperUser) {
            throw new Error('Shared Templates Are Read-Only. Use Save New for a Personal Copy.');
        }
        const conversion = previous && previous.scope !== scope;
        const id = previous && !conversion ? previous.template.id : shortid.generate();
        const entries = await this.list(underlying);
        if (previous && !entries.some(e => e.key === previous.key)) { throw new Error('Template No Longer Exists or Is No Longer Assigned.'); }
        if (entries.some(e => e.scope === scope && e.template.id !== id && e.template.name.toLowerCase() === name.toLowerCase())) {
            throw new Error('A Template With This Name Already Exists.');
        }
        const template: MavenSettingsTemplate = {version: 2, id, name, underlying, settings: copy(settings)};
        if (scope === 'shared') { await this._shared.save(template); }
        else { await this._settings.setValueConfirmed(mavenTemplatePrefix(underlying) + id, template); }
        // Destination first: a failed conversion must never destroy the only copy.
        if (conversion) {
            try { await this.remove(previous); }
            catch (e) { throw new Error('The New Copy Was Saved, but the Original Could Not Be Removed. Reload Templates Before Continuing.'); }
        }
        this.changed$.emit();
        return templateEntry(template, scope);
    }
    async remove(entry: MavenTemplateEntry): Promise<void> {
        const template = entry.template;
        if (entry.scope === 'shared') {
            if (!this._session.isSuperUser) { throw new Error('Shared Templates Are Read-Only.'); }
            await this._shared.remove(template.id);
        } else { await this._settings.deleteValueConfirmed(mavenTemplatePrefix(template.underlying) + template.id); }
        const ref = this._settings.getValue<MavenTemplateReference>(lastUsedKey(template.underlying));
        if (ref && ref.scope === entry.scope && ref.templateId === template.id) { await this.remember(template.underlying, null); }
        this.changed$.emit();
    }
}
